import type { ButtonHTMLAttributes, ReactNode } from 'react';
import type { Size, Variant } from '../types';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  /** Outline style (`btn-outline-*`). */
  outline?: boolean;
  /** Limitless flat style (`btn-flat-*`). */
  flat?: boolean;
  /** Pill (fully rounded) corners. */
  pill?: boolean;
  /** Phosphor icon class rendered before the label, e.g. "ph-check". */
  icon?: string;
  children?: ReactNode;
}

function colorClass(variant: Variant, outline: boolean, flat: boolean): string {
  if (flat) return `btn-flat-${variant}`;
  if (outline) return `btn-outline-${variant}`;
  return `btn-${variant}`;
}

export function Button({
  variant = 'primary',
  size = 'md',
  outline = false,
  flat = false,
  pill = false,
  icon,
  children,
  className = '',
  type = 'button',
  ...rest
}: ButtonProps) {
  const classes = [
    'btn',
    colorClass(variant, outline, flat),
    size === 'sm' ? 'btn-sm' : size === 'lg' ? 'btn-lg' : '',
    pill ? 'rounded-pill' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    // eslint-disable-next-line react/button-has-type
    <button type={type} className={classes} {...rest}>
      {icon && <i className={`${icon} ${children ? 'me-2' : ''}`} />}
      {children}
    </button>
  );
}
